import React, { useEffect, useRef, useState } from "react";
import styled, {
  keyframes,
  StyleSheetManager,
  createGlobalStyle
} from "styled-components";
import isPropValid from "@emotion/is-prop-valid";

const GlobalStyle = createGlobalStyle`
  html, body {
    overflow: hidden;
    margin: 0;
    padding: 0;
    height: 100%;
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  ::-webkit-scrollbar {
    display: none;  /* Chrome, Safari and Opera */
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(-11rem);
  }
  to {
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.125rem;
  width: calc(100vw - 4rem);
  height: calc(100vh - 4rem);
  padding: 2rem;
  overflow: hidden;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StatusText = styled.p`
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.5);
`;

const VoteItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NewVoteItem = styled(VoteItem)`
  animation: ${fadeIn} 0.5s cubic-bezier(0, 0, 0.2, 1) 0.2s forwards;
  opacity: 0;
`;

const VoteItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.125rem;
  animation: ${(props) => (props.isNewItem ? slideDown : "none")} 0.5s
    cubic-bezier(0, 0, 0.2, 1) forwards;
`;

const OptionsContainer = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  height: 11rem;
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`;

const ImageContainer = styled.div`
  height: 100%;
  min-width: 11rem;
  min-height: 11rem;
  border-radius: ${(props) =>
    props.percentage === 0
      ? "0.75rem"
      : props.left
      ? "0.75rem 0 0 0.75rem"
      : "0 0.75rem 0.75rem 0"};
  overflow: hidden;
  position: relative;
`;

const OptionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BarContainer = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
  flex: 1;
  display: flex;
  gap: 2rem;
`;

const BlurredBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;
  filter: blur(50px) brightness(0.8);
  transform: scale(2);
  width: 100%;
  height: 100%;
  min-width: 200px;
  min-height: 100px;
  z-index: 0;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
  }
`;

const Bar = styled.div`
  height: 100%;
  width: ${(props) => props.percentage}%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  overflow: hidden;
  ${(props) =>
    props.left
      ? `
      border-top-right-radius: 1rem;
      border-bottom-right-radius: 1rem;
    `
      : `
      border-top-left-radius: 1rem;
      border-bottom-left-radius: 1rem;
    `}
  ${(props) =>
    props.percentage === 0 &&
    `
    background-color: transparent;
  `}
`;

const BarLabel = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  font-weight: bold;
  font-size: 1.5rem;
  z-index: 2;
  display: flex;
  align-items: baseline;
  gap: 0.875rem;
  ${(props) => (props.left ? "left: 1.5rem;" : "right: 1.5rem;")}
  ${(props) => props.percentage === 0 && "display: none;"}
`;

const VoteCount = styled.span`
  font-weight: 400;
  opacity: 0.85;
`;

const MAX_VISIBLE = 6;

const preloadImages = (urls) =>
  Promise.all(
    urls.map(
      (url) =>
        new Promise((resolve, reject) => {
          if (!url) return resolve();
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        })
    )
  );

const PairsList = ({ pairs, isNewItem }) => (
  <VoteItemsContainer isNewItem={isNewItem}>
    {pairs.map((pair, index) => {
      const totalVotes = pair.option_1.count + pair.option_2.count;
      const option1Percentage =
        totalVotes > 0
          ? Math.round((pair.option_1.count / totalVotes) * 100)
          : 0;
      const option2Percentage =
        totalVotes > 0
          ? Math.round((pair.option_2.count / totalVotes) * 100)
          : 0;

      const VoteItemComponent = index === 0 ? NewVoteItem : VoteItem;

      return (
        <VoteItemComponent key={pair.pair_id}>
          <OptionsContainer>
            <Option>
              <ImageContainer left percentage={option1Percentage}>
                <OptionImage
                  src={pair.option_1.url}
                  alt={pair.option_1.value}
                />
              </ImageContainer>
            </Option>
            {totalVotes > 0 ? (
              <BarContainer>
                <Bar percentage={option1Percentage} left>
                  <BlurredBackground imageUrl={pair.option_1.url} />
                  <BarLabel left percentage={option1Percentage}>
                    <span>{option1Percentage}%</span>
                    <VoteCount>{pair.option_1.count}</VoteCount>
                  </BarLabel>
                </Bar>
                <Bar percentage={option2Percentage}>
                  <BlurredBackground imageUrl={pair.option_2.url} />
                  <BarLabel percentage={option2Percentage}>
                    <VoteCount>{pair.option_2.count}</VoteCount>
                    <span>{option2Percentage}%</span>
                  </BarLabel>
                </Bar>
              </BarContainer>
            ) : (
              <BarContainer>
                <Bar percentage={0} left>
                  <BarLabel left>0%</BarLabel>
                </Bar>
                <Bar percentage={0}>
                  <BarLabel>0%</BarLabel>
                </Bar>
              </BarContainer>
            )}
            <Option>
              <ImageContainer percentage={option2Percentage}>
                <OptionImage
                  src={pair.option_2.url}
                  alt={pair.option_2.value}
                />
              </ImageContainer>
            </Option>
          </OptionsContainer>
        </VoteItemComponent>
      );
    })}
  </VoteItemsContainer>
);

const WebSocketConnection = () => {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [pairs, setPairs] = useState([]);
  const [isNewItem, setIsNewItem] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [isInitialConnection, setIsInitialConnection] = useState(true);

  const handleNewVote = async (data) => {
    try {
      await preloadImages([data.option_1.url, data.option_2.url]);

      setPairs((prevPairs) => {
        const newPairs = [data, ...prevPairs];
        return newPairs.slice(0, MAX_VISIBLE);
      });
      setIsNewItem(true);
      setTimeout(() => setIsNewItem(false), 500);
    } catch (error) {
      console.error("Error preloading images:", error);
    }
  };

  const connectWebSocket = () => {
    const ws = new WebSocket(
      "wss://socket.this-or-that-machine-server.noshado.ws"
    );

    ws.onopen = () => {
      setConnectionStatus("connected");
      setReconnectAttempt(0);
      setIsInitialConnection(false);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "vote") {
          handleNewVote(data.data);
        }
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };

    ws.onerror = (error) => {
      setConnectionStatus("error");
      console.error("WebSocket error:", error);
    };

    ws.onclose = () => {
      setConnectionStatus("disconnected");
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);
      setTimeout(() => {
        setReconnectAttempt((prev) => prev + 1);
        connectWebSocket();
      }, delay);
    };

    setSocket(ws);
  };

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (socket) socket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let body;
  if (isInitialConnection && connectionStatus === "disconnected") {
    body = (
      <ConnectionStatus>
        <StatusText>Connecting to server...</StatusText>
      </ConnectionStatus>
    );
  } else if (connectionStatus === "error") {
    body = (
      <ConnectionStatus>
        <StatusText>Connection error. Attempting to reconnect...</StatusText>
      </ConnectionStatus>
    );
  } else if (pairs.length === 0) {
    body = (
      <ConnectionStatus>
        <StatusText>Waiting for votes...</StatusText>
      </ConnectionStatus>
    );
  } else {
    body = <PairsList pairs={pairs} isNewItem={isNewItem} />;
  }

  return (
    <StyleSheetManager shouldForwardProp={isPropValid}>
      <GlobalStyle />
      <Page>{body}</Page>
    </StyleSheetManager>
  );
};

// Fisher–Yates shuffle (in-place).
const shuffle = (arr) => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const Slideshow = ({ apiKey, intervalMs = 5000 }) => {
  const [allPairs, setAllPairs] = useState([]);
  const [visible, setVisible] = useState([]);
  const [isNewItem, setIsNewItem] = useState(false);
  const [loadState, setLoadState] = useState("loading");
  const queueRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    fetch(
      `https://this-or-that-machine-server.noshado.ws/votes/get-all-votes?limit=500&offset=0&key=${encodeURIComponent(
        apiKey
      )}`
    )
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(async (data) => {
        if (cancelled) return;
        const usable = (data.votes || []).filter(
          (p) => p.option_1?.url && p.option_2?.url
        );
        if (usable.length === 0) {
          setLoadState("empty");
          return;
        }
        const shuffled = shuffle([...usable]);
        // Pre-fill the visible list so the page isn't empty on first paint.
        const initial = shuffled.slice(0, MAX_VISIBLE);
        const rest = shuffled.slice(MAX_VISIBLE);
        try {
          await preloadImages(
            initial.flatMap((p) => [p.option_1.url, p.option_2.url])
          );
        } catch {
          // Even if some preloads fail, render — img onerror will catch the rest.
        }
        if (cancelled) return;
        setAllPairs(usable);
        queueRef.current = rest;
        setVisible(initial);
        setLoadState("ready");
      })
      .catch((err) => {
        console.error("Slideshow fetch error:", err);
        if (!cancelled) setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    if (loadState !== "ready") return;

    const advance = async () => {
      const currentIds = new Set(visible.map((p) => p.pair_id));

      // Refill the queue when it's exhausted.
      if (queueRef.current.length === 0) {
        queueRef.current = shuffle([...allPairs]);
      }

      // Pull the next candidate that isn't currently on screen.
      let next = null;
      const skipped = [];
      while (queueRef.current.length > 0) {
        const candidate = queueRef.current.shift();
        if (!currentIds.has(candidate.pair_id)) {
          next = candidate;
          break;
        }
        skipped.push(candidate);
      }
      // Skipped candidates go to the end so they get a turn later.
      queueRef.current.push(...skipped);

      // Edge case: only have MAX_VISIBLE or fewer pairs in total — at some
      // point every pool entry is on screen. Then there's no valid candidate;
      // just no-op this tick.
      if (!next) return;

      try {
        await preloadImages([next.option_1.url, next.option_2.url]);
      } catch {
        // If preload fails, still show — the <img> error fallback will trigger.
      }

      setVisible((prev) => [next, ...prev].slice(0, MAX_VISIBLE));
      setIsNewItem(true);
      setTimeout(() => setIsNewItem(false), 500);
    };

    const id = setInterval(advance, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadState, visible]);

  let body;
  if (loadState === "loading") {
    body = (
      <ConnectionStatus>
        <StatusText>Loading votes...</StatusText>
      </ConnectionStatus>
    );
  } else if (loadState === "error") {
    body = (
      <ConnectionStatus>
        <StatusText>Failed to load votes.</StatusText>
      </ConnectionStatus>
    );
  } else if (loadState === "empty" || visible.length === 0) {
    body = (
      <ConnectionStatus>
        <StatusText>No votes yet.</StatusText>
      </ConnectionStatus>
    );
  } else {
    body = <PairsList pairs={visible} isNewItem={isNewItem} />;
  }

  return (
    <StyleSheetManager shouldForwardProp={isPropValid}>
      <GlobalStyle />
      <Page>{body}</Page>
    </StyleSheetManager>
  );
};

const API_BASE = "https://this-or-that-machine-server.noshado.ws";

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const LoginInput = styled.input`
  font-size: 1.25rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  width: 22rem;
  max-width: 80vw;
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const LoginButton = styled.button`
  font-size: 1.25rem;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  border: none;
  background: white;
  color: black;
  cursor: pointer;
  font-weight: 600;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const LoginError = styled.div`
  color: #ff8080;
  font-size: 1rem;
`;

const Login = ({ onAuthed }) => {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const key = value.trim();
    if (!key) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(
        `${API_BASE}/auth/validate-api-key?key=${encodeURIComponent(key)}`
      );
      const data = await r.json();
      if (r.ok && data.valid) {
        localStorage.setItem("apiKey", key);
        onAuthed(key);
      } else {
        setError("Invalid API key");
      }
    } catch (err) {
      setError("Could not reach server");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <LoginForm onSubmit={handleSubmit}>
        <StatusText>Enter API key</StatusText>
        <LoginInput
          type="password"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="API key"
        />
        <LoginButton type="submit" disabled={submitting || !value.trim()}>
          {submitting ? "Checking..." : "Continue"}
        </LoginButton>
        {error && <LoginError>{error}</LoginError>}
      </LoginForm>
    </Page>
  );
};

const useApiKey = () => {
  const [state, setState] = useState({
    apiKey: null,
    status: "checking" // "checking" | "needs_login" | "ready"
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("apiKey");
    const stored = localStorage.getItem("apiKey");
    const candidate = fromUrl || stored;

    if (!candidate) {
      setState({ apiKey: null, status: "needs_login" });
      return;
    }

    let cancelled = false;
    fetch(
      `${API_BASE}/auth/validate-api-key?key=${encodeURIComponent(candidate)}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.valid) {
          // Cache to localStorage so future visits without the URL param work.
          localStorage.setItem("apiKey", candidate);
          setState({ apiKey: candidate, status: "ready" });
        } else {
          // If the bad key came from the URL, leave any stored key alone —
          // only clear stored when the stored one itself is bad.
          if (!fromUrl) localStorage.removeItem("apiKey");
          setState({ apiKey: null, status: "needs_login" });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ apiKey: null, status: "needs_login" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onAuthed = (key) => setState({ apiKey: key, status: "ready" });
  return { ...state, onAuthed };
};

const SlideshowGate = () => {
  const { apiKey, status, onAuthed } = useApiKey();

  if (status === "checking") {
    return (
      <StyleSheetManager shouldForwardProp={isPropValid}>
        <GlobalStyle />
        <Page>
          <ConnectionStatus>
            <StatusText>Loading...</StatusText>
          </ConnectionStatus>
        </Page>
      </StyleSheetManager>
    );
  }

  if (status === "needs_login") {
    return (
      <StyleSheetManager shouldForwardProp={isPropValid}>
        <GlobalStyle />
        <Login onAuthed={onAuthed} />
      </StyleSheetManager>
    );
  }

  return <Slideshow apiKey={apiKey} />;
};

const App = () => {
  const path = window.location.pathname.replace(/\/+$/, "");
  const isSlideshow = path === "/slideshow";
  return isSlideshow ? <SlideshowGate /> : <WebSocketConnection />;
};

export default App;
